import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Home, FileEdit, Users, Eye, MessageSquare, TrendingUp, Inbox, Plus, ArrowUpRight,
} from "lucide-react";
import api from "@/lib/api";
import AdminLayout from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";

const CARDS = [
  { key: "published", label: "Case pubblicate", icon: Home, color: "text-brand-teal", bg: "bg-brand-tealLight" },
  { key: "draft", label: "Case in bozza", icon: FileEdit, color: "text-brand-gold", bg: "bg-amber-50" },
  { key: "owners", label: "Proprietari", icon: Users, color: "text-brand-plum", bg: "bg-brand-warm" },
  { key: "visits", label: "Visite mini-siti", icon: Eye, color: "text-brand-teal", bg: "bg-brand-tealLight" },
  { key: "inquiries", label: "Richieste prenotazione", icon: MessageSquare, color: "text-brand-plum", bg: "bg-brand-warm" },
  { key: "conversion", label: "Conversione", icon: TrendingUp, color: "text-brand-teal", bg: "bg-brand-tealLight", suffix: "%" },
  { key: "contacts", label: "Richieste di contatto", icon: Inbox, color: "text-brand-gold", bg: "bg-amber-50" },
];

export default function Dashboard() {
  const [stats, setStats] = useState({});
  const [props, setProps] = useState([]);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/properties").then((r) => setProps(r.data)).catch(() => {});
  }, []);

  return (
    <AdminLayout title="Dashboard">
      <div className="mb-8">
        <div className="rounded-3xl bg-brand-violet p-8 text-white flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="font-serif text-2xl md:text-3xl">Benvenuto in Quattro Accommodations</h2>
            <p className="text-white/60 mt-1">Gestisci strutture, proprietari e mini-siti da un unico posto.</p>
          </div>
          <Link to="/admin/properties/new">
            <Button data-testid="dashboard-new-property" className="rounded-full bg-brand-teal hover:bg-brand-tealDark text-brand-violet font-semibold px-6 h-12">
              <Plus size={18} className="mr-1" /> Nuova casa vacanza
            </Button>
          </Link>
        </div>
      </div>

      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="dashboard-kpis">
        {CARDS.map((c) => (
          <div key={c.key} className="rounded-2xl bg-white p-6 border border-brand-plum/8 shadow-sm">
            <div className="flex items-center justify-between">
              <span className={`w-11 h-11 rounded-xl ${c.bg} flex items-center justify-center`}><c.icon className={c.color} size={20} /></span>
              {c.key === "contacts" && stats.new_contacts > 0 && (
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-brand-teal text-brand-violet">{stats.new_contacts} nuove</span>
              )}
            </div>
            <p className="font-serif text-4xl text-brand-plum mt-4">{stats[c.key] ?? 0}{c.suffix && (stats[c.key] ?? 0) !== 0 ? c.suffix : ""}</p>
            <p className="text-sm text-brand-plum/50 mt-1">{c.label}</p>
          </div>
        ))}
      </div>

      <div className="mt-8 rounded-2xl bg-white border border-brand-plum/8 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-brand-plum/8">
          <h3 className="font-serif text-xl text-brand-plum">Strutture recenti</h3>
          <Link to="/admin/properties" className="text-sm text-brand-teal hover:underline flex items-center gap-1">Vedi tutte <ArrowUpRight size={14} /></Link>
        </div>
        <div className="divide-y divide-brand-plum/8">
          {props.slice(0, 5).map((p) => (
            <Link key={p.id} to={`/admin/properties/${p.id}/edit`} className="flex items-center gap-4 p-4 hover:bg-brand-warm/50 transition-colors">
              <img src={(p.photos?.find((x) => x.is_main) || p.photos?.[0])?.url} alt="" className="w-14 h-14 rounded-xl object-cover" />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-brand-plum truncate">{p.name}</p>
                <p className="text-sm text-brand-plum/50">{p.city} · Ospitato da {p.owner?.first_name}</p>
              </div>
              <StatusBadge status={p.status} />
            </Link>
          ))}
          {props.length === 0 && <p className="p-8 text-center text-brand-plum/40">Nessuna struttura ancora.</p>}
        </div>
      </div>
    </AdminLayout>
  );
}

export function StatusBadge({ status }) {
  const map = {
    pubblicata: ["Pubblicata", "bg-brand-tealLight text-brand-tealDark"],
    bozza: ["Bozza", "bg-amber-50 text-brand-gold"],
    non_pubblicata: ["Non pubblicata", "bg-slate-100 text-slate-500"],
    sospesa: ["Sospesa", "bg-red-50 text-red-500"],
  };
  const [label, cls] = map[status] || map.bozza;
  return <span className={`text-xs font-medium px-2.5 py-1 rounded-full shrink-0 ${cls}`}>{label}</span>;
}
